import { useState, useEffect, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import { Location } from '../types';
import Modal from './Modal';
import StockChangeModal from './StockChangeModal';
import { SkeletonTable } from './Skeleton';
import { EmptyStateNoData, EmptyStateError } from './EmptyState';
import { formatDate, formatDateTime, getMadridDate, toDateInputValue } from '../utils/dateFormatter';
import SearchableSelect from './SearchableSelect';

// Función helper para obtener el color del stock según el mínimo
const getStockColor = (stock: number, minStock: number): string => {
  if (stock < minStock) {
    return 'var(--error)'; // Rojo: por debajo del mínimo
  } else if (stock <= minStock * 1.2) {
    return 'var(--warning)'; // Amarillo: igual o hasta 20% por encima del mínimo
  } else {
    return 'var(--success)'; // Verde: más de 20% por encima del mínimo
  }
};

// Función helper para verificar si mostrar icono de alerta (stock < 50% del mínimo)
const shouldShowAlert = (stock: number, minStock: number): boolean => {
  return stock < minStock * 0.5;
};

interface Need {
  stone_id: number;
  cod_piedra: string;
  nombre_piedra: string;
  current_stock: number;
  min_stock: number;
  needed: number;
  pending_stock?: number; // Stock pendiente de pedidos
  is_top_ventas?: boolean;
  proveedor?: string | null;
}

interface PurchaseOrderPiedras {
  id: number;
  code: string;
  proveedor: string;
  status: 'PENDING' | 'RECEIVED' | 'CANCELLED';
  created_at: string;
  expected_at?: string;
  lines: Array<{
    id: number;
    stone_id: number;
    qty_ordered: number;
    qty_received: number;
    stone: {
      id: number;
      cod_piedra: string;
      nombre_piedra: string;
    };
  }>;
}

export default function PurchaseOrdersPiedras() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [orders, setOrders] = useState<PurchaseOrderPiedras[]>([]);
  const [needs, setNeeds] = useState<Need[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrderPiedras | null>(null);
  const [selectedLines, setSelectedLines] = useState<Map<number, number>>(new Map());
  const [formData, setFormData] = useState({ proveedor: '', expected_at: '' });
  const [lineQtyInputs, setLineQtyInputs] = useState<Map<number, string>>(new Map());
  const [selectedPeriod, setSelectedPeriod] = useState<number>(1); // Meses
  const [loadingNeeds, setLoadingNeeds] = useState(false);
  const [manuallyAddedPieces, setManuallyAddedPieces] = useState<Set<number>>(new Set());
  const [allStones, setAllStones] = useState<Array<{ id: number; cod_piedra: string; nombre_piedra: string; proveedor?: string | null }>>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isStockChangeModalOpen, setIsStockChangeModalOpen] = useState(false);
  const [stockChanges, setStockChanges] = useState<Array<{ item_code: string; item_name: string; location_name: string; stock_before: number; stock_after: number; quantity_changed: number }>>([]);
  const [isReceivingAll, setIsReceivingAll] = useState(false);

  const visibleNeeds = useMemo(() => {
    return needs.filter(need => {
      // Siempre mostrar si está seleccionado
      if (selectedLines.has(need.stone_id)) return true;

      // Siempre mostrar si fue agregado manualmente
      if (manuallyAddedPieces.has(need.stone_id)) return true;

      // Calcular si se necesita stock para el período seleccionado
      const requiredStockForPeriod = need.min_stock * selectedPeriod;
      const pendingStock = need.pending_stock || 0;
      const totalStock = need.current_stock + pendingStock;

      return totalStock < requiredStockForPeriod;
    });
  }, [needs, selectedPeriod, selectedLines, manuallyAddedPieces]);

  const selectableNeedIds = visibleNeeds.map((need) => need.stone_id);
  const allNeedsSelected =
    selectableNeedIds.length > 0 && selectableNeedIds.every((id) => selectedLines.has(id));

  useEffect(() => {
    loadOrders();
    loadNeeds();
  }, []);


  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/purchase-orders-piedras');
      setOrders(response.data);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Error al cargar pedidos';
      showError(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadNeeds = async () => {
    try {
      setLoadingNeeds(true);
      const response = await api.get('/purchase-orders-piedras/needs');
      const needsData = response.data || [];
      setNeeds(needsData);
      if (needsData.length === 0) {
        console.warn('[PurchaseOrdersPiedras] No se encontraron piedras con stock mínimo configurado');
      }
    } catch (err: any) {
      console.error('[PurchaseOrdersPiedras] Error al cargar necesidades:', err);
      const errorMessage = err.response?.data?.error || 'Error al cargar piedras disponibles';
      console.error('[PurchaseOrdersPiedras] Detalles del error:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      showError(errorMessage);
      setNeeds([]);
    } finally {
      setLoadingNeeds(false);
    }
  };

  const loadAllStones = async () => {
    try {
      const response = await api.get('/stones');
      setAllStones(response.data || []);
    } catch (err: any) {
      console.error('Error al cargar todas las piedras:', err);
    }
  };


  const handleManualAddStone = async (stone: { id: number; cod_piedra: string; nombre_piedra: string }) => {
    // Verificar si la piedra ya está en needs
    const existingNeed = needs.find(n => n.stone_id === stone.id);
    if (existingNeed) {
      setManuallyAddedPieces(prev => new Set([...prev, stone.id]));
      const newMap = new Map(selectedLines);
      const defaultQty = calculateDefaultQty(existingNeed);
      newMap.set(stone.id, defaultQty > 0 ? defaultQty : 10);
      setSelectedLines(newMap);
    } else {
      // Si no existe, consultar stock real del backend
      try {
        // Cargar ubicaciones si no están cargadas
        let allLocations = locations;
        if (allLocations.length === 0) {
          const locationsResponse = await api.get('/locations');
          allLocations = locationsResponse.data;
          setLocations(allLocations);
        }

        const almacenPiedras = allLocations.find((l: Location) => l.code === 'ALMACEN_PIEDRAS');
        const almacenPiedrasId = almacenPiedras?.id;

        // Consultar stock real
        const stockResponse = await api.get('/stock', {
          params: {
            item_type: 'STONE',
            item_id: stone.id,
            location_id: almacenPiedrasId
          }
        });
        const stockData = stockResponse.data;
        const currentStock = Array.isArray(stockData) && stockData.length > 0
          ? parseFloat(stockData[0].qty) || 0
          : 0;

        // Consultar mínimo de stock (para piedras, location_id es NULL)
        const minStockResponse = await api.get('/min-stock', {
          params: {
            item_type: 'STONE',
            item_id: stone.id,
            location_id: 'null'
          }
        });
        const minStockData = minStockResponse.data;
        const minStock = Array.isArray(minStockData) && minStockData.length > 0
          ? parseFloat(minStockData[0].min_qty_base) || 0
          : 0;

        // Obtener multiplicador
        const multiplierResponse = await api.get('/settings/min-stock-multiplier');
        const multiplier = parseFloat(multiplierResponse.data.value || '1');
        const effectiveMin = Math.round(minStock * multiplier);

        // Obtener información adicional de la piedra (proveedor, is_top_ventas)
        const stoneInfo = allStones.find(s => s.id === stone.id);

        // Crear necesidad con valores reales
        // Nota: pending_stock se calculará cuando se recarguen las necesidades completas
        const newNeed: Need = {
          stone_id: stone.id,
          cod_piedra: stone.cod_piedra,
          nombre_piedra: stone.nombre_piedra,
          current_stock: currentStock,
          min_stock: effectiveMin,
          needed: Math.max(0, effectiveMin - currentStock) || 10, // Cantidad sugerida basada en necesidad real
          pending_stock: 0, // Se actualizará cuando se recarguen las necesidades
          is_top_ventas: false, // Se puede obtener del backend si es necesario
          proveedor: stoneInfo?.proveedor || null,
        };
        setNeeds(prev => [...prev, newNeed]);
        setManuallyAddedPieces(prev => new Set([...prev, stone.id]));
        const newMap = new Map(selectedLines);
        newMap.set(stone.id, newNeed.needed);
        setSelectedLines(newMap);
      } catch (err: any) {
        console.error('Error al cargar stock para piedra manual:', err);
        // Fallback: crear necesidad con valores por defecto
        const newNeed: Need = {
          stone_id: stone.id,
          cod_piedra: stone.cod_piedra,
          nombre_piedra: stone.nombre_piedra,
          current_stock: 0,
          min_stock: 0,
          needed: 10,
        };
        setNeeds(prev => [...prev, newNeed]);
        setManuallyAddedPieces(prev => new Set([...prev, stone.id]));
        const newMap = new Map(selectedLines);
        newMap.set(stone.id, 10);
        setSelectedLines(newMap);
      }
    }
    showSuccess(`${stone.cod_piedra} agregado manualmente`);
  };

  const calculateDefaultQty = (need: Need, period?: number): number => {
    const periodToUse = period ?? selectedPeriod;

    // El mínimo se establece para 30 días (1 mes)
    // Para el período seleccionado, necesitamos: min_stock * (período_en_meses)
    const requiredStockForPeriod = need.min_stock * periodToUse;

    // Stock total disponible (actual + pendiente)
    const pendingStock = need.pending_stock || 0;
    const totalStock = need.current_stock + pendingStock;

    // Si el stock cubre el mínimo para el período, no sugerir nada
    if (totalStock >= requiredStockForPeriod) {
      return 0;
    }

    // Si estamos por debajo o igual al mínimo, calcular cantidad necesaria
    const needed = requiredStockForPeriod - totalStock;

    // Aplicar margen de seguridad del 20% (1.2x)
    const safetyMargin = 1.2;
    const suggestedQty = Math.ceil(needed * safetyMargin);

    return suggestedQty;
  };

  const handleOpenCreateModal = async () => {
    // Primero, abrir el modal inmediatamente para que el usuario vea algo
    setIsCreateModalOpen(true);

    try {
      setSelectedLines(new Map());
      setManuallyAddedPieces(new Set());
      setSelectedPeriod(1);
      await loadAllStones();

      // Pre-llenar fecha esperada con 15 días desde hoy (en zona horaria de Madrid)
      // No pre-llenar proveedor, dejar que el usuario seleccione
      try {
        const today = getMadridDate();
        const expectedDate = new Date(today);
        expectedDate.setDate(today.getDate() + 15);
        const expectedDateStr = toDateInputValue(expectedDate);
        setFormData({ proveedor: '', expected_at: expectedDateStr });
      } catch (dateErr) {
        console.error('[PurchaseOrdersPiedras] Error al formatear fecha:', dateErr);
        // Si falla el formateo de fecha, usar valores por defecto
        setFormData({ proveedor: '', expected_at: '' });
      }

      // Recargar necesidades después de abrir el modal para tener datos actualizados
      try {
        await loadNeeds();
      } catch (err) {
        console.error('[PurchaseOrdersPiedras] Error al cargar necesidades:', err);
        // No bloqueamos la apertura del modal si falla la carga
      }
    } catch (err) {
      console.error('[PurchaseOrdersPiedras] Error en handleOpenCreateModal:', err);
      // El modal ya está abierto, así que no hacemos nada más
    }
  };

  const handlePeriodChange = (months: number) => {
    setSelectedPeriod(months);
    // Recalcular cantidades basadas en el período seleccionado
    // Solo para las piedras que ya estaban seleccionadas
    const newMap = new Map<number, number>();

    // Mantener solo las líneas que ya estaban seleccionadas
    // Recalcular sus cantidades basadas en el nuevo período
    selectedLines.forEach((qty, stoneId) => {
      const need = needs.find(n => n.stone_id === stoneId);
      if (need) {
        // Recalcular cantidad basada en el nuevo período
        const defaultQty = calculateDefaultQty(need, months);
        newMap.set(stoneId, defaultQty);
      } else {
        // Si la piedra no está en needs (fue agregada manualmente), mantener la cantidad actual
        newMap.set(stoneId, qty);
      }
    });

    setSelectedLines(newMap);
  };

  const handleToggleLine = (need: Need) => {
    const newMap = new Map(selectedLines);
    if (newMap.has(need.stone_id)) {
      newMap.delete(need.stone_id);
    } else {
      const defaultQty = calculateDefaultQty(need);
      newMap.set(need.stone_id, defaultQty);
    }
    setSelectedLines(newMap);
  };

  const toggleSelectAllNeeds = () => {
    const newMap = new Map(selectedLines);
    if (allNeedsSelected) {
      selectableNeedIds.forEach((id) => newMap.delete(id));
    } else {
      selectableNeedIds.forEach((id) => {
        const need = needs.find((n) => n.stone_id === id);
        if (need) {
          const defaultQty = calculateDefaultQty(need);
          newMap.set(id, defaultQty);
        }
      });
    }
    setSelectedLines(newMap);
  };

  const handleSelectTopVentas = () => {
    const newMap = new Map(selectedLines);
    const topVentasNeeds = needs.filter((n) => n.is_top_ventas && selectableNeedIds.includes(n.stone_id));

    topVentasNeeds.forEach((need) => {
      const defaultQty = calculateDefaultQty(need);
      newMap.set(need.stone_id, defaultQty);
    });

    setSelectedLines(newMap);
  };

  const handleUpdateQty = (stoneId: number, qty: number) => {
    const newMap = new Map(selectedLines);
    if (qty > 0) {
      newMap.set(stoneId, qty);
    } else {
      newMap.delete(stoneId);
    }
    setSelectedLines(newMap);
  };

  const handleCreateOrder = async () => {
    if (selectedLines.size === 0) {
      showError('Debes seleccionar al menos una línea');
      return;
    }
    if (!formData.proveedor.trim()) {
      showError('El nombre del proveedor es obligatorio');
      return;
    }

    try {
      const lines = Array.from(selectedLines.entries()).map(([stone_id, qty_ordered]) => ({
        stone_id,
        qty_ordered,
      }));

      await api.post('/purchase-orders-piedras', {
        proveedor: formData.proveedor,
        expected_at: formData.expected_at || undefined,
        lines,
      });
      showSuccess('Pedido de piedras creado correctamente');
      setIsCreateModalOpen(false);
      setSelectedLines(new Map());
      loadOrders();
      loadNeeds();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al crear pedido');
    }
  };

  const handleOpenDetailModal = async (order: PurchaseOrderPiedras) => {
    try {
      const response = await api.get(`/purchase-orders-piedras/${order.id}`);
      const orderData = response.data;
      setSelectedOrder(orderData);

      // Pre-llenar los campos de cantidad con la cantidad pendiente
      const newQtyInputs = new Map<number, string>();
      orderData.lines.forEach((line: any) => {
        const pending = line.qty_ordered - line.qty_received;
        if (pending > 0) {
          newQtyInputs.set(line.id, pending.toString());
        }
      });
      setLineQtyInputs(newQtyInputs);

      setIsDetailModalOpen(true);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar detalles');
    }
  };

  const handleDownloadPDF = async (orderId: number, orderCode: string) => {
    try {
      const response = await api.get(`/purchase-orders-piedras/${orderId}/pdf`, {
        responseType: 'blob',
      });

      // Verificar si la respuesta es un error (JSON en lugar de PDF)
      if (response.data instanceof Blob && response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        showError(errorData.error || 'Error al generar PDF');
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pedido-piedras-${orderCode}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('PDF generado correctamente');
    } catch (err: any) {
      console.error('Error al descargar PDF:', err);
      let errorMessage = 'Error al generar PDF';

      if (err.response?.data) {
        if (err.response.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.error || errorMessage;
          } catch {
            errorMessage = err.message || errorMessage;
          }
        } else {
          errorMessage = err.response.data.error || errorMessage;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      showError(errorMessage);
    }
  };

  const handleReceiveLine = async (orderId: number, lineId: number, qty: number) => {
    if (qty <= 0) {
      showError('La cantidad debe ser mayor a 0');
      return;
    }

    try {
      const response = await api.post(`/purchase-orders-piedras/${orderId}/lines/${lineId}/receive`, {
        qty_received: qty,
      });
      showSuccess('Línea recibida correctamente');

      if (response.data.stockChanges && response.data.stockChanges.length > 0) {
        setStockChanges(response.data.stockChanges);
        setIsStockChangeModalOpen(true);
      }

      loadOrders();
      if (selectedOrder) {
        const orderResponse = await api.get(`/purchase-orders-piedras/${orderId}`);
        setSelectedOrder(orderResponse.data);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al recibir línea');
    }
  };

  const handleReceiveAll = async () => {
    if (!selectedOrder || isReceivingAll) return;

    try {
      setIsReceivingAll(true);

      // Recibir todas las líneas pendientes
      const pendingLines = selectedOrder.lines.filter(
        (line) => line.qty_ordered - line.qty_received > 0
      );

      if (pendingLines.length === 0) {
        showError('No hay líneas pendientes para recibir');
        setIsReceivingAll(false);
        return;
      }

      // Recibir cada línea pendiente y acumular cambios de stock
      const allStockChanges: Array<{ item_code: string; item_name: string; location_name: string; stock_before: number; stock_after: number; quantity_changed: number }> = [];

      for (const line of pendingLines) {
        const pending = line.qty_ordered - line.qty_received;
        if (pending > 0) {
          const response = await api.post(`/purchase-orders-piedras/${selectedOrder.id}/lines/${line.id}/receive`, {
            qty_received: pending,
          });

          if (response.data.stockChanges && response.data.stockChanges.length > 0) {
            allStockChanges.push(...response.data.stockChanges);
          }
        }
      }

      showSuccess(`Todas las líneas recibidas correctamente (${pendingLines.length} línea(s))`);

      if (allStockChanges.length > 0) {
        setStockChanges(allStockChanges);
        setIsStockChangeModalOpen(true);
      }

      loadOrders();
      if (selectedOrder) {
        const response = await api.get(`/purchase-orders-piedras/${selectedOrder.id}`);
        setSelectedOrder(response.data);
        setLineQtyInputs(new Map());
      }

      // Cerrar el modal después de recibir todo
      setIsDetailModalOpen(false);
      setSelectedOrder(null);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al recibir todas las líneas');
    } finally {
      setIsReceivingAll(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este pedido?')) {
      return;
    }

    try {
      await api.delete(`/purchase-orders-piedras/${id}`);
      showSuccess('Pedido eliminado correctamente');
      loadOrders();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar pedido');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'var(--warning)';
      case 'RECEIVED':
        return 'var(--success)';
      case 'CANCELLED':
        return 'var(--error)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'RECEIVED':
        return 'Recibido';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="tab-content">
        <SkeletonTable rows={8} columns={7} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <EmptyStateError message={error} onRetry={loadOrders} />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="flex-between mb-2">
        <h2>Pedidos de Piedras</h2>
        {canEdit('piedras-pedidos') && (
          <button
            className="btn btn-primary"
            onClick={() => handleOpenCreateModal()}
          >
            + Nuevo Pedido
          </button>
        )}
      </div>


      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Proveedor</th>
              <th>Estado</th>
              <th>Líneas</th>
              <th>Fecha Creación</th>
              <th>Fecha Esperada</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center p-0">
                  <EmptyStateNoData onCreate={canEdit('piedras-pedidos') ? handleOpenCreateModal : undefined} />
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id}>
                  <td><strong>{order.code}</strong></td>
                  <td>{order.proveedor}</td>
                  <td>
                    <span style={{ color: getStatusColor(order.status) }}>
                      {getStatusLabel(order.status)}
                    </span>
                  </td>
                  <td>
                    {order.lines.length} línea(s)
                    <br />
                    <small style={{ color: 'var(--text-secondary)' }}>
                      {order.lines.reduce((sum, l) => sum + l.qty_ordered, 0)} unidades
                    </small>
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>
                    {order.expected_at
                      ? formatDate(order.expected_at)
                      : '-'}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleOpenDetailModal(order)}
                        style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                      >
                        Ver
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleDownloadPDF(order.id, order.code)}
                        style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                        title="Generar PDF"
                      >
                        PDF
                      </button>
                      {canEdit('piedras-pedidos') && (
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDelete(order.id)}
                          style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Crear - Similar a fundición pero con piedras */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Nuevo Pedido de Piedras"
        size="large"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setIsCreateModalOpen(false)}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreateOrder}
              disabled={selectedLines.size === 0 || !formData.proveedor.trim()}
              data-modal-default="true"
            >
              Crear Pedido
            </button>
          </>
        }
      >
        <div className="input-group">
          <label className="input-label">Proveedor *</label>
          <input
            type="text"
            className={`input ${!formData.proveedor.trim() ? 'input-error' : ''}`}
            value={formData.proveedor}
            onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })}
            placeholder="Ingresa el nombre del proveedor"
            required
          />
          {!formData.proveedor.trim() && (
            <small style={{ color: 'var(--error)', marginTop: '0.25rem', display: 'block' }}>
              Debes ingresar un proveedor
            </small>
          )}
        </div>

        <div className="input-group">
          <label className="input-label">Fecha Esperada</label>
          <input
            type="date"
            className="input"
            value={formData.expected_at}
            onChange={(e) => setFormData({ ...formData, expected_at: e.target.value })}
          />
        </div>

        <div className="input-group">
          <label className="input-label">Período de Cobertura</label>
          <select
            className="input"
            value={selectedPeriod}
            onChange={(e) => handlePeriodChange(parseInt(e.target.value, 10))}
          >
            <option value={1}>1 mes</option>
            <option value={2}>2 meses</option>
            <option value={3}>3 meses</option>
            <option value={4}>4 meses</option>
            <option value={6}>6 meses</option>
            <option value={12}>12 meses</option>
          </select>
          <small style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', display: 'block' }}>
            Las cantidades se calcularán automáticamente basándose en el stock mínimo y el período seleccionado.
          </small>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Líneas del Pedido</h3>
          <button
            className="btn btn-secondary"
            onClick={handleSelectTopVentas}
            style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
            disabled={visibleNeeds.filter(n => n.is_top_ventas).length === 0}
          >
            Seleccionar Top Ventas
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <SearchableSelect<{ id: number; cod_piedra: string; nombre_piedra: string; proveedor?: string | null }>
            items={allStones}
            onSelect={handleManualAddStone}
            getItemLabel={(item) => item.nombre_piedra}
            getItemId={(item) => item.id}
            getItemCode={(item) => item.cod_piedra}
            placeholder="Buscar piedra..."
            label="Agregar Piedra Manualmente"
            excludeIds={new Set([...selectedLines.keys()])}
          />
        </div>
        {loadingNeeds ? (
          <div className="table-container" style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-secondary)' }}>Cargando piedras disponibles...</p>
          </div>
        ) : needs.length === 0 ? (
          <div className="table-container" style={{ padding: '2rem' }}>
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
              <p style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>No hay piedras disponibles para pedir</p>
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Para poder hacer pedidos, las piedras deben tener un stock mínimo configurado en la sección "Stock Mínimo".
              </p>
            </div>
          </div>
        ) : visibleNeeds.length === 0 ? (
          <div className="table-container" style={{ padding: '2rem' }}>
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
              <p style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>Todo el stock está cubierto</p>
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                No es necesario pedir ninguna piedra para el período seleccionado ({selectedPeriod} {selectedPeriod === 1 ? 'mes' : 'meses'}).
              </p>
            </div>
          </div>
        ) : (
          <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={allNeedsSelected}
                      disabled={selectableNeedIds.length === 0}
                      onChange={toggleSelectAllNeeds}
                    />
                  </th>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Stock Origen<br /><small>(Almacén Actual)</small></th>
                  <th>Stock Destino<br /><small>(Almacén después)</small></th>
                  <th>Mínimo</th>
                  <th>Necesario</th>
                  <th>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {visibleNeeds.map((need) => {
                  const isSelected = selectedLines.has(need.stone_id);
                  const defaultQty = calculateDefaultQty(need);
                  const qty = selectedLines.get(need.stone_id) || defaultQty;
                  return (
                    <tr key={need.stone_id} style={{ backgroundColor: need.is_top_ventas ? 'rgba(76, 175, 80, 0.1)' : undefined }}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleLine(need)}
                        />
                      </td>
                      <td>
                        {need.cod_piedra}
                        {need.is_top_ventas && (
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--accent)' }} title="Top Ventas">
                            ⭐
                          </span>
                        )}
                      </td>
                      <td>{need.nombre_piedra}</td>
                      <td style={{ textAlign: 'center' }}>
                        {(() => {
                          const pendingStock = need.pending_stock || 0;
                          const totalStock = need.current_stock + pendingStock;
                          return (
                            <>
                              <strong style={{ color: getStockColor(totalStock, need.min_stock) }}>
                                {need.current_stock}
                              </strong>
                              {pendingStock > 0 && (
                                <span style={{ marginLeft: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }} title={`Stock pendiente: ${pendingStock}`}>
                                  (+{pendingStock})
                                </span>
                              )}
                              {shouldShowAlert(totalStock, need.min_stock) && (
                                <span style={{ marginLeft: '0.25rem', fontSize: '1rem' }} title="Stock crítico: menos del 50% del mínimo">
                                  <AlertTriangle size={16} strokeWidth={1.5} />
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {(() => {
                          const pendingStock = need.pending_stock || 0;
                          const qtyToOrder = selectedLines.get(need.stone_id) || 0;
                          const stockDespues = need.current_stock + pendingStock + qtyToOrder;
                          return (
                            <>
                              <strong style={{ color: getStockColor(stockDespues, need.min_stock) }}>
                                {stockDespues}
                              </strong>
                              {shouldShowAlert(stockDespues, need.min_stock) && (
                                <span style={{ marginLeft: '0.25rem', fontSize: '1rem' }} title="Stock crítico: menos del 50% del mínimo">
                                  <AlertTriangle size={16} strokeWidth={1.5} />
                                </span>
                              )}
                              <br />
                              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                                (+{qtyToOrder})
                              </small>
                            </>
                          );
                        })()}
                      </td>
                      <td style={{ textAlign: 'center' }}>{need.min_stock}</td>
                      <td style={{ textAlign: 'center' }}>
                        {(() => {
                          const requiredForPeriod = need.min_stock * selectedPeriod;
                          const pendingStock = need.pending_stock || 0;
                          const totalStock = need.current_stock + pendingStock;
                          const needed = Math.max(0, requiredForPeriod - totalStock);
                          return (
                            <strong style={{ color: needed > 0 ? 'var(--error)' : 'var(--success)' }}>
                              {needed > 0 ? needed : 'Cubierto'}
                            </strong>
                          );
                        })()}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          className="input"
                          value={isSelected ? qty : ''}
                          onChange={(e) =>
                            handleUpdateQty(need.stone_id, parseInt(e.target.value, 10) || 0)
                          }
                          disabled={!isSelected}
                          placeholder={isSelected ? '' : defaultQty.toString()}
                          style={{ width: '80px' }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {selectedLines.size > 0 && (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <strong>Total seleccionado: {selectedLines.size} línea(s)</strong>
            <br />
            <small style={{ color: 'var(--text-secondary)' }}>
              {Array.from(selectedLines.values()).reduce((sum, qty) => sum + qty, 0)} unidades
            </small>
          </div>
        )}
      </Modal>

      {/* Modal Detalle - Similar a fundición */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedOrder(null);
        }}
        title={selectedOrder ? `Pedido: ${selectedOrder.code}` : 'Detalle'}
        size="large"
        footer={
          <button
            className="btn btn-secondary"
            onClick={() => {
              setIsDetailModalOpen(false);
              setSelectedOrder(null);
            }}
          >
            Cerrar
          </button>
        }
      >
        {selectedOrder && (
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <p><strong>Proveedor:</strong> {selectedOrder.proveedor}</p>
              <p>
                <strong>Estado:</strong>{' '}
                <span style={{ color: getStatusColor(selectedOrder.status) }}>
                  {getStatusLabel(selectedOrder.status)}
                </span>
              </p>
              <p>
                <strong>Fecha Creación:</strong>{' '}
                {formatDateTime(selectedOrder.created_at)}
              </p>
              {selectedOrder.expected_at && (
                <p>
                  <strong>Fecha Esperada:</strong>{' '}
                  {formatDateTime(selectedOrder.expected_at)}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Líneas</h3>
              {selectedOrder.lines.some((line) => line.qty_ordered - line.qty_received > 0) && (
                <button
                  className="btn btn-primary"
                  onClick={handleReceiveAll}
                  disabled={isReceivingAll}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  {isReceivingAll ? 'Recibiendo...' : 'Recibir Todo'}
                </button>
              )}
            </div>
            <div className="table-container" style={{ overflowX: 'auto' }}>
              <table className="table" style={{ minWidth: '700px', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: '200px' }}>Piedra</th>
                    <th style={{ minWidth: '80px', textAlign: 'center' }}>Pedido</th>
                    <th style={{ minWidth: '80px', textAlign: 'center' }}>Recibido</th>
                    <th style={{ minWidth: '80px', textAlign: 'center' }}>Pendiente</th>
                    <th style={{ minWidth: '200px' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.lines.map((line) => {
                    const pending = line.qty_ordered - line.qty_received;
                    const qtyInput = lineQtyInputs.get(line.id) || '';
                    return (
                      <tr key={line.id}>
                        <td>
                          <strong>{line.stone.cod_piedra}</strong>
                          <br />
                          <small style={{ color: 'var(--text-secondary)' }}>
                            {line.stone.nombre_piedra}
                          </small>
                        </td>
                        <td style={{ textAlign: 'center' }}>{line.qty_ordered}</td>
                        <td style={{ textAlign: 'center' }}>
                          <strong style={{ color: 'var(--success)' }}>{line.qty_received}</strong>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <strong style={{ color: pending > 0 ? 'var(--warning)' : 'var(--success)' }}>
                            {pending}
                          </strong>
                        </td>
                        <td>
                          {pending > 0 && selectedOrder.status !== 'RECEIVED' ? (
                            <div className="flex gap-1" style={{ alignItems: 'center', flexWrap: 'nowrap' }}>
                              <input
                                type="number"
                                min="1"
                                max={pending}
                                className="input"
                                value={qtyInput}
                                onChange={(e) => {
                                  const newMap = new Map(lineQtyInputs);
                                  newMap.set(line.id, e.target.value);
                                  setLineQtyInputs(newMap);
                                }}
                                placeholder="Cant."
                                style={{ width: '90px', minWidth: '90px' }}
                              />
                              <button
                                className="btn btn-primary"
                                onClick={() => {
                                  const qty = parseInt(qtyInput, 10);
                                  if (qty > 0 && qty <= pending) {
                                    handleReceiveLine(selectedOrder.id, line.id, qty);
                                    // No eliminar el input inmediatamente, mantenerlo por si hay más recepciones
                                    // Solo limpiar si la línea está completa
                                    const newMap = new Map(lineQtyInputs);
                                    const newPending = pending - qty;
                                    if (newPending <= 0) {
                                      newMap.delete(line.id);
                                    } else {
                                      newMap.set(line.id, newPending.toString());
                                    }
                                    setLineQtyInputs(newMap);
                                  } else if (qty > 0) {
                                    showError(`La cantidad no puede ser mayor a ${pending}`);
                                  } else {
                                    showError('La cantidad debe ser mayor a 0');
                                  }
                                }}
                                style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}
                              >
                                Recibir
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: 'var(--text-secondary)' }}>Completo</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>

      <StockChangeModal
        isOpen={isStockChangeModalOpen}
        onClose={() => setIsStockChangeModalOpen(false)}
        title="Cambios de Stock - Pedido de Piedras Recibido"
        changes={stockChanges}
      />
    </div>
  );
}
